import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import API from '../utils/gasClient';
import { Loading } from '../components/Loading';
import { HomePage } from './HomePage';
import { TicketSelectionPageContainer } from './TicketSelectionPageContainer';
import { CheckoutPageContainer } from './CheckoutPageContainer';
import { PaymentPage } from './PaymentPage';
import { ConfirmationPage } from './ConfirmationPage';
import { EditOrderPage } from './EditOrderPage';
import { EditOrderFormPage } from './EditOrderFormPage';

export const EventPage = ({ eventSlug }) => {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estado do pedido com persistência no localStorage
  const [pedido, setPedido] = useState(() => {
    // Tenta recuperar do localStorage ao inicializar
    const savedPedido = localStorage.getItem(`pedido_${eventSlug}`);
    if (savedPedido) {
      try {
        return JSON.parse(savedPedido);
      } catch (e) {
        return {
          ingressos: {},
          total: 0,
          dadosComprador: {
            nome: '',
            email: '',
            comprovante: null
          },
          participantes: []
        };
      }
    }
    return {
      ingressos: {},
      total: 0,
      dadosComprador: {
        nome: '',
        email: '',
        comprovante: null
      },
      participantes: []
    };
  });
  
  // Salva no localStorage sempre que o pedido mudar
  useEffect(() => {
    if (pedido && Object.keys(pedido.ingressos).length > 0) {
      // Remove o comprovante antes de salvar (é um File object)
      const pedidoParaSalvar = {
        ...pedido,
        dadosComprador: {
          ...pedido.dadosComprador,
          comprovante: null // Não salva o arquivo
        }
      };
      localStorage.setItem(`pedido_${eventSlug}`, JSON.stringify(pedidoParaSalvar));
    }
  }, [pedido, eventSlug]);
  
  const navigate = useNavigate();

  // Busca dados do evento
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        
        // Sempre busca da API para ter dados completos (incluindo cards)
        const eventData = await API.getEvent(eventSlug);
        
        if (!eventData) {
          throw new Error('Evento não encontrado');
        }

        if (!eventData.active) {
          throw new Error('Este evento não está mais ativo');
        }

        setEvent(eventData);
        
        // Aplica tema do evento
        applyEventTheme(eventData);
        
        setLoading(false);
      } catch (err) {
        console.error('❌ Erro ao buscar evento:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventSlug]);

  // Aplica tema do evento
  const applyEventTheme = (eventData) => {
    if (eventData.primaryColor) {
      document.documentElement.style.setProperty('--primary-color', eventData.primaryColor);
    }
    if (eventData.secondaryColor) {
      document.documentElement.style.setProperty('--secondary-color', eventData.secondaryColor);
    }
    
    // Atualiza título da página
    document.title = eventData.eventName;
  };

  // Funções de navegação
  const handleNavigate = (page, data = null) => {
    // Constrói path absoluto: /eventSlug/page
    const cleanPage = page.replace(/^\//, '');
    const fullPath = `/${eventSlug}/${cleanPage}`;
    navigate(fullPath, { state: data });
  };

  const handleVoltar = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-3xl font-display text-red-600 mb-4">
            {error}
          </h2>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary text-white rounded-full hover:bg-primary-hover transition-colors duration-300"
          >
            ← Voltar para Eventos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Routes>
        <Route 
          path="/" 
          element={
            <HomePage 
              event={event} 
              onNavigate={handleNavigate}
            />
          } 
        />
        <Route 
          path="/selecao" 
          element={
            <TicketSelectionPageContainer 
              event={event}
              pedido={pedido}
              setPedido={setPedido}
              onNavigate={handleNavigate}
              onVoltar={handleVoltar}
            />
          } 
        />
        <Route 
          path="/checkout" 
          element={
            <CheckoutPageContainer 
              event={event}
              pedido={pedido}
              setPedido={setPedido}
              onNavigate={handleNavigate}
              onVoltar={handleVoltar}
            />
          } 
        />
        <Route 
          path="/pagamento" 
          element={
            <PaymentPage 
              event={event}
              pedido={pedido}
              onNavigate={handleNavigate}
              onVoltar={handleVoltar}
            />
          } 
        />
        <Route 
          path="/confirmacao" 
          element={
            <ConfirmationPage 
              event={event}
              pedido={pedido}
              orderId={pedido?.orderId}
              onNavigate={handleNavigate}
            />
          } 
        />
        <Route 
          path="/editOrder" 
          element={
            <EditOrderPage 
              event={event}
              onNavigate={handleNavigate}
              onVoltar={handleVoltar}
            />
          } 
        />
        <Route 
          path="/edit-form" 
          element={
            <EditOrderFormPageWrapper 
              event={event}
              onNavigate={handleNavigate}
              onVoltar={handleVoltar}
            />
          } 
        />
      </Routes>
    </div>
  );
};

// Wrapper para EditOrderFormPage para pegar o state
function EditOrderFormPageWrapper({ event, onNavigate, onVoltar }) {
  const location = useLocation();
  const orderData = location.state; // CORRIGIDO: era location.state.data

  if (!orderData) {
    // Se não tem orderData, volta para editOrder
    onNavigate('editOrder');
    return null;
  }

  return (
    <EditOrderFormPage
      event={event}
      orderData={orderData}
      onNavigate={onNavigate}
      onVoltar={onVoltar}
    />
  );
}
